import { jsx as _jsx } from "react/jsx-runtime";
import { getInProgressItem, getInProgressItemModifierGroup, getItemModifierGroupById, NotificationType } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { AnimatePresence } from 'framer-motion';
import { observer } from 'mobx-react';
import { useContext } from 'react';
import { useI18n } from '../../../context/occ/hooks/use-i18n';
import { getIdForComponentNotification } from '../../../utils/get-id-for-component-notification';
import { Accordion } from '../../accordion/accordion';
import { AccordionSection } from '../../accordion/subcomponents/section';
import { HeadingLevel } from '../../heading/heading';
import { Notification } from '../../notification/notification';
import { ItemPickerContextInstance } from '../types/context';
import { getLimitedQuantityRequiredText, getUnlimitedQuantityRequiredText } from '../utils/get-required-text';
import { ItemPickerModifierGroup } from './modifier-group';
const b = bemClassBuilder('v-item-picker-modifier-groups');
export const ItemPickerModifierGroups = observer(props => {
    const i18n = useI18n();
    const { baseItem, modifierGroups } = props;
    const { config, displayText } = useContext(ItemPickerContextInstance);
    const { showExpandedByDefault } = config.normalItemModifiers;
    return (_jsx(Accordion, Object.assign({ className: b.block(), headingLevel: HeadingLevel.H5 }, { children: modifierGroups.map(group => {
            const modifierGroup = getItemModifierGroupById(group.itemModifierGroupId);
            if (!modifierGroup)
                throw new Error(`Cannot find modifier group with ID: ${group.itemModifierGroupId}`);
            return (_jsx(AccordionSection, Object.assign({ id: modifierGroup.id, title: getPromptTextForGroup(modifierGroup, displayText, i18n), subtitle: getRequiredText(modifierGroup, displayText, i18n), expandedByDefault: showExpandedByDefault, error: getNotificationForGroup(modifierGroup, displayText, i18n) }, { children: _jsx(ItemPickerModifierGroup, { modifierGroup: modifierGroup, baseItem: baseItem }) }), modifierGroup.id));
        }) })));
});
ItemPickerModifierGroups.displayName = 'ItemPickerModifierGroups';
const getRequiredText = (modifierGroup, displayText, i18n) => {
    const { quantityLimit } = modifierGroup;
    const { minimum } = quantityLimit;
    if (quantityLimit.isUnlimited)
        return getUnlimitedQuantityRequiredText(minimum, displayText, i18n);
    const inProgressModifierGroup = getInProgressItemModifierGroup(modifierGroup.id);
    const selectedQuantity = inProgressModifierGroup.selectedQuantity;
    return getLimitedQuantityRequiredText(minimum, quantityLimit.maximum, displayText, selectedQuantity, i18n);
};
const getNotificationForGroup = (group, displayText, i18n) => {
    var _a, _b;
    const inProgressItemModifierGroup = getInProgressItemModifierGroup(group.id);
    if (!inProgressItemModifierGroup)
        return;
    const message = i18n.template(displayText.insufficientModifiersSelectedMessage, {
        minimum: group.quantityLimit.minimum
    });
    const isValidSelection = (_b = (_a = getInProgressItem()) === null || _a === void 0 ? void 0 : _a.initiallyDisplayAsValid) !== null && _b !== void 0 ? _b : true;
    const show = !isValidSelection && !inProgressItemModifierGroup.minimumReached;
    return (_jsx(AnimatePresence, { children: show && (_jsx(Notification, { id: getIdForComponentNotification('item-picker', NotificationType.Error, 'invalid-selection'), messages: [message], type: NotificationType.Error, wrapperClassName: "v-item-picker-option-selection-notification", displayInline: true })) }));
};
const getPromptTextForGroup = (group, displayText, i18n) => {
    var _a;
    return (_a = i18n.translatable(group.promptText)) !== null && _a !== void 0 ? _a : displayText.modifierGroupDefaultPromptText;
};
